import type { ReactNode } from "react";

/**
 * The agent writes markdown; a `<pre>` shows it as a wall. Owner, 2026-10-02: *"show the md stylized."* So this
 * renders the subset agent reports actually use — headings, emphasis, inline and fenced code, lists, quotes, rules
 * and pipe tables — with the desk's own type scale and colour tokens.
 *
 * It parses to React elements, never to HTML: an agent report is untrusted text and must not be able to become markup.
 */

const INLINE =
  /(`[^`]+`)|(\*\*[^*]+\*\*)|(__[^_]+__)|(~~[^~]+~~)|(\*[^*\n]+\*)|(_[^_\n]+_)|(\[[^\]]+\]\([^)\s]+\))/;

export function inlineNodes(text: string, keyPrefix = "i"): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  let n = 0;
  while (rest.length) {
    const m = INLINE.exec(rest);
    if (!m || m.index === undefined) {
      out.push(rest);
      break;
    }
    if (m.index > 0) out.push(rest.slice(0, m.index));
    const token = m[0];
    const key = `${keyPrefix}-${n++}`;
    if (token.startsWith("`")) {
      out.push(
        <code
          key={key}
          className="rounded bg-[color:var(--bg-tertiary)] px-1 py-0.5 font-mono text-[0.9em]"
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith("**") || token.startsWith("__")) {
      out.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("~~")) {
      out.push(<s key={key}>{token.slice(2, -2)}</s>);
    } else if (token.startsWith("[")) {
      const split = token.indexOf("](");
      const href = token.slice(split + 2, -1);
      if (!/^(https?:|mailto:)/i.test(href) && !/^\/(?![/\\])[^\s\\]*$/.test(href)) {
        out.push(token);
        rest = rest.slice(m.index + token.length);
        continue;
      }
      out.push(
        <a key={key} href={href} className="underline" rel="noreferrer noopener" target="_blank">
          {token.slice(1, split)}
        </a>,
      );
    } else {
      out.push(<em key={key}>{token.slice(1, -1)}</em>);
    }
    rest = rest.slice(m.index + token.length);
  }
  return out;
}

const isTableSeparator = (line: string) =>
  /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(line);

const cells = (line: string) =>
  line
    .replace(/^\s*\|/, "")
    .replace(/\|\s*$/, "")
    .split("|")
    .map((c) => c.trim());

/** Markdown → desk-styled elements. Anything unrecognised stays its own plain text. */
export function Prose({ text, className }: { text: string; className?: string }) {
  const lines = (text ?? "").replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let para: string[] = [];
  let k = 0;

  const flush = () => {
    const body = para.join("\n").trim();
    para = [];
    if (!body) return;
    blocks.push(
      <p key={`p${k++}`} dir="auto" className="mb-2 whitespace-pre-wrap last:mb-0">
        {inlineNodes(body, `p${k}`)}
      </p>,
    );
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? "";

    if (/^\s*```/.test(line)) {
      flush();
      const body: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```/.test(lines[i] ?? "")) body.push(lines[i++] ?? "");
      blocks.push(
        <pre
          key={`c${k++}`}
          className="mb-2 overflow-x-auto rounded-md border border-[color:var(--divider)] bg-[color:var(--bg-secondary)] p-2 font-mono text-xs"
        >
          {body.join("\n")}
        </pre>,
      );
      continue;
    }

    if (!line.trim()) {
      flush();
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      flush();
      const level = (heading[1] ?? "").length;
      blocks.push(
        <p
          key={`h${k++}`}
          dir="auto"
          className={`mt-3 mb-1 font-semibold first:mt-0 ${level <= 2 ? "text-base" : "text-sm"}`}
        >
          {inlineNodes((heading[2] ?? "").replace(/\s*#+\s*$/, ""), `h${k}`)}
        </p>,
      );
      continue;
    }

    if (/^\s*([-*_])\s*\1\s*\1[\s\-*_]*$/.test(line)) {
      flush();
      blocks.push(<hr key={`r${k++}`} className="my-3 border-[color:var(--divider)]" />);
      continue;
    }

    if (/^\s*>/.test(line)) {
      flush();
      const quoted: string[] = [];
      while (i < lines.length && /^\s*>/.test(lines[i] ?? ""))
        quoted.push((lines[i++] ?? "").replace(/^\s*>\s?/, ""));
      i--;
      blocks.push(
        <blockquote
          key={`q${k++}`}
          className="mb-2 border-[color:var(--divider)] border-s-2 bg-[color:var(--bg-secondary)] px-2 py-1"
        >
          <Prose text={quoted.join("\n")} />
        </blockquote>,
      );
      continue;
    }

    if (
      /^\s*\|.*\|\s*$/.test(line) &&
      i + 1 < lines.length &&
      isTableSeparator(lines[i + 1] ?? "")
    ) {
      flush();
      const head = cells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && /^\s*\|/.test(lines[i] ?? "")) rows.push(cells(lines[i++] ?? ""));
      i--;
      blocks.push(
        <table key={`t${k++}`} className="mb-2 border-collapse text-xs">
          <thead>
            <tr>
              {head.map((c, ci) => (
                <th
                  key={ci}
                  className="border border-[color:var(--divider)] bg-[color:var(--bg-secondary)] px-2 py-1 text-start font-semibold"
                >
                  {inlineNodes(c, `th${ci}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri}>
                {r.map((c, ci) => (
                  <td key={ci} className="border border-[color:var(--divider)] px-2 py-1">
                    {inlineNodes(c, `td${ri}-${ci}`)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>,
      );
      continue;
    }

    const bullet = /^\s*([-*+]|\d+[.)])\s+(.*)$/.exec(line);
    if (bullet) {
      flush();
      const ordered = /\d/.test(bullet[1] ?? "");
      const items: string[] = [];
      while (i < lines.length) {
        const m = /^\s*([-*+]|\d+[.)])\s+(.*)$/.exec(lines[i] ?? "");
        if (!m) break;
        items.push(m[2] ?? "");
        i++;
      }
      i--;
      const List = ordered ? "ol" : "ul";
      blocks.push(
        <List key={`l${k++}`} className={`mb-2 ps-5 ${ordered ? "list-decimal" : "list-disc"}`}>
          {items.map((item, ii) => (
            <li key={ii} dir="auto" className="my-0.5">
              {inlineNodes(item, `li${ii}`)}
            </li>
          ))}
        </List>,
      );
      continue;
    }

    para.push(line);
  }
  flush();

  return <div className={`text-sm leading-relaxed ${className ?? ""}`}>{blocks}</div>;
}
